import { useEffect } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { authApi } from "@/api";
import type {
  ChangePasswordData,
  EmailData,
  LoginData,
  ResetPasswordData,
  UpdateProfileData,
} from "@/api";
import { QUERY_KEYS, isStaffRole } from "@/constants";
import { useCrossTabAuthSync, useSessionKeepAlive } from "@/lib/auth/keep-alive";
import { hardLogout } from "@/lib/session";
import { useAuthStore } from "@/store";

/**
 * Eslatma: API xatolari axios javob interceptor'ida global toast bilan
 * ko'rsatiladi, shuning uchun bu hook'lar o'z `onError` toast'ini
 * qo'shmaydi.
 */

export const useLogin = () => {
  const setAuth = useAuthStore(state => state.setAuth);

  return useMutation({
    mutationFn: (data: LoginData) => authApi.login(data),
    onSuccess: res => {
      setAuth(res.data);
    },
  });
};

export const useForgotPassword = () =>
  useMutation({ mutationFn: (data: EmailData) => authApi.forgotPassword(data) });

export const useResetPassword = () =>
  useMutation({ mutationFn: (data: ResetPasswordData) => authApi.resetPassword(data) });

export const useLogout = () => {
  const logout = useAuthStore(state => state.logout);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (refreshToken: string) => authApi.logout({ refreshToken }),
    // So'rov tugagach tozalanadi (muvaffaqiyatli ham, yiqilgan ham):
    // avvalroq tozalasak, so'rov token'siz ketardi. Kesh ham tozalanadi —
    // aks holda o'sha tabda boshqa hisobga kirilganda oldingi hisobning
    // ro'yxatlari ekranga chiqib qolardi (`placeholderData`).
    onSettled: () => {
      logout();
      queryClient.clear();
    },
  });
};

export const useMe = (enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_ME],
    queryFn: () => authApi.me(),
    enabled,
    select: res => res.data,
  });

/** O'z profilini saqlash: sarlavhadagi ism ham darhol yangilanadi. */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const setUser = useAuthStore(state => state.setUser);

  return useMutation({
    mutationFn: (data: UpdateProfileData) => authApi.updateProfile(data),
    onSuccess: res => {
      setUser(res.data);
      queryClient.setQueryData([QUERY_KEYS.GET_ME], res);
    },
  });
};

// ─── Xavfsizlik: parol va sessiyalar ─────────────────────────────────────────

export const useChangePassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChangePasswordData) => authApi.changePassword(data),
    // Backend parol o'zgarganda boshqa sessiyalarni bekor qiladi, ya'ni
    // qurilmalar ro'yxati darhol eskiradi.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_SESSIONS] });
    },
  });
};

/** Joriy foydalanuvchining faol sessiyalari (qurilmalari). */
export const useSessions = () => {
  const refreshToken = useAuthStore(state => state.data?.refreshToken);

  return useQuery({
    // Refresh token kalitning bir qismi: u header'da yuboriladi va backend
    // "shu qurilma" bayrog'ini o'shanga qarab qo'yadi. Kalitda bo'lmasa,
    // jimgina yangilanishdan keyin ro'yxat eski token bilan hisoblangan
    // holicha keshda qolardi.
    queryKey: [QUERY_KEYS.GET_SESSIONS, refreshToken],
    queryFn: () => authApi.listSessions(refreshToken),
    select: res => res.data,
  });
};

export const useRevokeSession = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => authApi.revokeSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_SESSIONS] });
      toast.success(t("SECURITY.DEVICE_REVOKED"));
    },
  });
};

/** Barcha sessiyalarni bekor qiladi — joriy qurilma ham. */
export const useLogoutAll = () => useMutation({ mutationFn: () => authApi.logoutAll() });

/**
 * Ilova ishga tushganda (va sessiya ochilganda) `/auth/me` ni qayta o'qib,
 * foydalanuvchini do'konga yozadi: ism, rasm va ROL yangi bo'lib turadi.
 *
 * Ikkita holatda sessiya shu yerda tugaydi:
 *  1. Hisob bloklangan — ochiq tab hech narsa sezmasdan keshdan ishlayverardi.
 *  2. Rol xodimlikdan chiqarilgan — panel ochiq qolib, har bir so'rov 403
 *     bilan qaytardi va odam nima bo'layotganini tushunmasdi.
 */
export const useAuthBootstrap = () => {
  const auth = useAuthStore(state => state.auth);
  const rehydrating = useAuthStore(state => state.rehydrating);
  const hasToken = useAuthStore(state => Boolean(state.data?.accessToken));
  const setUser = useAuthStore(state => state.setUser);
  const { t } = useTranslation();

  // Token tiklanmaguncha `/auth/me` KETMAYDI. Ilgari u reload'da `auth`
  // bayrog'iga qarab darhol ketardi, token'siz 401 olardi va ikkinchi
  // yangilashni boshlab yuborardi — sessiya shu poygada yopilardi.
  const { data } = useMe(auth && !rehydrating && hasToken);

  useSessionKeepAlive();
  useCrossTabAuthSync();

  useEffect(() => {
    if (!data) return;

    if (data.status === "blocked") {
      toast.error(t("ERRORS.ACCOUNT_BLOCKED"));
      hardLogout();
      return;
    }

    if (!isStaffRole(data.role)) {
      toast.error(t("AUTH.STAFF_ONLY"));
      hardLogout();
      return;
    }

    setUser(data);
  }, [data, setUser, t]);
};
